import { Tooltip } from '@/components/ui/tooltip';
import type { Transfer } from '@/types/payout';
import { Box, Flex } from '@chakra-ui/react';
import { PayoutTransferStatus } from '@fluenceur/types';
import { InfoCircle } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import TransferStatusBadge from './TransferStatusBadge';
import { resolveTransferDisplayStatus, TRANSFER_NOT_SENT } from './transferDisplayState';

interface TransferStatusHintProps {
  content: string;
  color: string;
}

const TransferStatusHint: React.FC<TransferStatusHintProps> = ({ content, color }) => (
  <Tooltip content={content} showArrow>
    <Box as="span" cursor="help" color={color}>
      <InfoCircle width={14} height={14} />
    </Box>
  </Tooltip>
);

/**
 * A transfer's status in the payout drawer, with the reason behind it when
 * there is one. A transfer the closure of an unpaid payout stopped reads as not
 * sent, in neutral tones: the provider's failure styling is kept for money that
 * was really refused.
 */
const TransferStatusCell: React.FC<{ transfer: Transfer }> = ({ transfer }) => {
  const { t } = useTranslation();
  const displayStatus = resolveTransferDisplayStatus(transfer);

  return (
    <Flex align="center" gap={1}>
      <TransferStatusBadge status={displayStatus} size="sm" />
      {displayStatus === TRANSFER_NOT_SENT && (
        <TransferStatusHint content={t('payouts.transferStatus.notSentReason')} color="fg.muted" />
      )}
      {displayStatus === PayoutTransferStatus.FAILED && transfer.failureReason && (
        <TransferStatusHint content={transfer.failureReason} color="red.500" />
      )}
    </Flex>
  );
};

export default TransferStatusCell;
